/**
 * Texto completo do contrato de prestação de serviços LuxStay Madeira.
 *
 * Componente partilhado entre:
 *  - ContractSignPage (cliente lê ANTES de assinar)
 *  - ClientContractView (cliente revê o que assinou no menu da sua área)
 *
 * Mantém uma única fonte de verdade para o conteúdo legal — qualquer
 * alteração ao contrato é refletida em ambos os locais.
 *
 * Versão do contrato: 1.0
 */

import { Building2, Sparkles, Image as ImageIcon } from "lucide-react";
import ProviderParty, { contractBrand } from "./ProviderParty";

export const CONTRACT_VERSION = "1.0";

const SERVICE_LABELS = {
  cleaning:    "Limpeza",
  supervision: "Supervisão",
  checkin:     "Preparação Check-in",
  laundry:     "Lavandaria",
  reports:     "Relatórios de supervisão",
};

function Clause({ n, title, children }) {
  return (
    <div className="space-y-1.5">
      <h3 className="font-display text-[#d4af37] text-sm">
        Cláusula {n}.ª · {title}
      </h3>
      <div className="text-[13px] text-[#cfd8d6] leading-relaxed text-justify space-y-1.5">
        {children}
      </div>
    </div>
  );
}

/**
 * Renders all contract clauses + parties + properties + services + image
 * authorization decision (read-only). Selectable text — client can copy.
 *
 * Props:
 *   contract — the contract document (with .client, .properties,
 *              .global_scope, .services, .image_authorization, .notes)
 */
export default function ContractFullText({ contract }) {
  const client = contract?.client || {};
  const includedProps = (contract?.properties || []).filter(p => p.included);
  const imgAuthYes = contract?.image_authorization === "yes";
  const imgAuthNo  = contract?.image_authorization === "no";

  return (
    <div className="space-y-5" data-testid="contract-full-text">
      {/* Title */}
      <div className="text-center pb-3 border-b border-[#1a4640]">
        <div className="text-[10px] tracking-[0.18em] uppercase text-[#7c8e8b]">
          Contrato de Prestação de Serviços
        </div>
        <h2 className="mt-1 font-display text-xl text-[#fdfbf7]">
          Limpeza, Supervisão e Gestão Operacional<br />
          <span className="italic text-[#d4af37]">de Alojamento Local</span>
        </h2>
        <div className="mt-1 text-[11px] text-[#7c8e8b]">
          Funchal · Madeira · Portugal · Versão {CONTRACT_VERSION}
        </div>
      </div>

      {/* Preamble */}
      <p className="text-[13px] text-[#cfd8d6] leading-relaxed text-justify italic border-l-2 border-[#7c8e8b]/40 pl-3">
        O presente contrato é celebrado entre as partes adiante identificadas
        e tem por objeto a prestação de serviços profissionais de limpeza,
        supervisão, preparação para hóspedes, lavandaria e relatórios
        operacionais, em propriedades destinadas a alojamento local e/ou
        arrendamento turístico de curta duração na Região Autónoma da Madeira.
      </p>

      {/* Identification of parties */}
      <div>
        <h3 className="font-display text-[#d4af37] text-sm mb-2">Identificação das Partes</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <ProviderParty contract={contract} className="rounded-md border border-[#1a4640] p-3" />
          <div className="rounded-md border border-[#1a4640] p-3">
            <div className="text-[10px] tracking-[0.14em] uppercase text-[#d4af37] font-semibold">
              Segunda Outorgante · Cliente
            </div>
            <div className="mt-1 font-display text-base text-[#fdfbf7]">{client.name || "—"}</div>
            <div className="mt-2 text-[11.5px] text-[#cfd8d6] leading-relaxed">
              <strong>NIF:</strong> {client.nif || "—"}<br />
              <strong>Morada:</strong> {client.address || "—"}<br />
              <strong>Email:</strong> {client.email || "—"}<br />
              <strong>Telefone:</strong> {client.phone || "—"}
            </div>
          </div>
        </div>
      </div>

      <Clause n="1" title="Objeto do Contrato">
        <p>1.1. A {contractBrand(contract)} obriga-se a prestar à Segunda Outorgante (Cliente) os serviços profissionais discriminados na Cláusula 3.ª, com vista à manutenção, higienização, supervisão operacional e preparação das propriedades identificadas na Cláusula 2.ª para a sua utilização em regime de alojamento local, Airbnb, Booking.com, Vrbo ou plataformas equivalentes.</p>
        <p>1.2. Os serviços prestados respeitam os mais elevados padrões de qualidade do setor de hospitalidade premium, em conformidade com as boas práticas de higiene, saúde pública e proteção do consumidor aplicáveis em Portugal.</p>
      </Clause>

      <div>
        <h3 className="font-display text-[#d4af37] text-sm mb-2 flex items-center gap-1.5">
          <Building2 size={14} /> Cláusula 2.ª · Propriedades Abrangidas
        </h3>
        {contract?.global_scope ? (
          <div className="rounded-md border border-[#d4af37]/40 bg-[#d4af37]/5 p-3 text-[12px] text-[#e8c860]">
            Aplica-se a <strong>todas as propriedades atuais e futuras</strong> associadas à conta do Cliente.
          </div>
        ) : includedProps.length === 0 ? (
          <p className="text-[12px] text-[#7c8e8b] italic">Nenhuma propriedade selecionada.</p>
        ) : (
          <ul className="divide-y divide-[#1a4640] rounded-md border border-[#1a4640] overflow-hidden">
            {includedProps.map((p, i) => (
              <li key={i} className="px-3 py-2 grid sm:grid-cols-3 gap-1 text-[12px]">
                <span className="text-[#fdfbf7] font-medium">{p.name || "—"}</span>
                <span className="text-[#a0b2af]">{p.code || "—"}</span>
                <span className="text-[#a0b2af]">{p.address || "—"}</span>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-2 text-[11.5px] text-[#7c8e8b] text-justify">
          2.1. Qualquer alteração, adição ou remoção de propriedades deverá
          ser comunicada por escrito (email aceite) e formalizada em aditamento,
          salvo se o Cliente tiver optado pelo âmbito global.
        </p>
      </div>

      <div>
        <h3 className="font-display text-[#d4af37] text-sm mb-2 flex items-center gap-1.5">
          <Sparkles size={14} /> Cláusula 3.ª · Serviços Contratados
        </h3>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(SERVICE_LABELS).map(([k, label]) => {
            const on = !!contract?.services?.[k];
            return (
              <span key={k} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] border ${
                on ? "bg-[#d4af37]/10 border-[#d4af37]/50 text-[#e8c860]"
                   : "bg-transparent border-[#1a4640] text-[#7c8e8b] line-through"
              }`}>
                {on && "✓ "}{label}
              </span>
            );
          })}
        </div>
        <p className="mt-3 text-[12px] text-[#cfd8d6]">
          <strong className="text-[#fdfbf7]">3.1. Descrição detalhada:</strong>
        </p>
        <ul className="mt-1.5 space-y-1.5 text-[12px] text-[#cfd8d6] list-disc pl-5">
          <li><strong>Limpeza:</strong> higienização integral da propriedade (divisões comuns, quartos, casas de banho, cozinha) com produtos profissionais; substituição de roupa de cama e toalhas; preparação visual para o próximo hóspede.</li>
          <li><strong>Supervisão:</strong> inspeção pormenorizada do estado geral, identificação de avarias, danos ou itens em falta, com relatório fotográfico.</li>
          <li><strong>Preparação Check-in:</strong> reposição de consumíveis (papel higiénico, sabonetes, amenities), verificação de eletrodomésticos, ajuste de termostatos.</li>
          <li><strong>Lavandaria:</strong> recolha, lavagem profissional, secagem e dobragem de roupa de cama e toalhas.</li>
          <li><strong>Relatórios:</strong> envio de relatório digital com checklist, fotos antes/depois, ocorrências e estado final, em até 24h após cada serviço.</li>
        </ul>
      </div>

      <Clause n="4" title="Preços e Condições de Pagamento">
        <p>4.1. Os preços dos serviços seguem a tabela vigente da {contractBrand(contract)}, disponível em <em>luxstaymadeira.pt/precos</em>, sendo aplicáveis os escalões correspondentes à tipologia de cada propriedade (T0, T1, T2, T3, T4 ou Villa).</p>
        <p>4.2. Aos preços indicados acresce IVA à taxa legal em vigor, quando aplicável.</p>
        <p>4.3. Os pagamentos são efetuados, regra geral, <strong>após a conclusão do serviço</strong>, no prazo máximo de 5 (cinco) dias úteis, por <strong>transferência bancária</strong> ou <strong>MB WAY</strong>.</p>
        <p>4.4. Em propriedades premium ou serviços de elevada dimensão, poderá ser exigido sinal não inferior a 30% do valor total estimado, antecipadamente.</p>
        <p>4.5. O incumprimento dos prazos de pagamento confere à {contractBrand(contract)} o direito de suspender a prestação de serviços até regularização.</p>
      </Clause>

      <Clause n="5" title="Vigência, Cancelamento e Rescisão">
        <p>5.1. O presente contrato vigora a partir da data da sua assinatura digital e mantém-se em vigor por tempo indeterminado.</p>
        <p>5.2. O Cliente poderá cancelar serviços ou rescindir o contrato mediante <strong>aviso prévio mínimo de 15 (quinze) dias</strong>, por escrito (email para luxstaymadeira@gmail.com).</p>
        <p>5.3. Cancelamentos comunicados com prazo inferior a 48 horas poderão estar sujeitos à <strong>cobrança parcial (50%)</strong> ou <strong>integral (100%)</strong> dos serviços previamente agendados.</p>
        <p>5.4. A {contractBrand(contract)} reserva-se o direito de rescindir, com aviso prévio de 30 dias, em caso de incumprimento reiterado por parte do Cliente.</p>
      </Clause>

      <Clause n="6" title="Obrigações do Cliente">
        <ol className="list-decimal pl-5 space-y-1">
          <li>Garantir o acesso atempado e desimpedido às propriedades, fornecendo chaves, códigos de acesso ou caixas-de-chaves operacionais.</li>
          <li>Comunicar com antecedência razoável as datas e horários de check-in/check-out dos hóspedes.</li>
          <li>Manter as propriedades em condições estruturais adequadas (água, luz, gás, Internet, eletrodomésticos funcionais).</li>
          <li>Suportar os custos de consumíveis, produtos de limpeza específicos solicitados e/ou eventuais reparações fora do escopo do serviço.</li>
          <li>Tratar a equipa da {contractBrand(contract)} com respeito, abstendo-se de exigências abusivas ou discriminatórias.</li>
        </ol>
      </Clause>

      <Clause n="7" title={`Obrigações da ${contractBrand(contract)}`}>
        <ol className="list-decimal pl-5 space-y-1">
          <li>Prestar os serviços com diligência, qualidade e pontualidade, dentro dos prazos acordados.</li>
          <li>Garantir a confidencialidade absoluta sobre informações do Cliente, dos seus hóspedes e das propriedades, em conformidade com o RGPD.</li>
          <li>Reportar de imediato qualquer dano observado, objeto esquecido ou anomalia detetada na propriedade.</li>
          <li>Possuir e manter equipa qualificada, devidamente equipada e supervisionada.</li>
          <li>Disponibilizar canal de contacto (telefone, WhatsApp, email) durante o horário laboral (Seg–Sáb · 07:00–19:00).</li>
        </ol>
      </Clause>

      <Clause n="8" title="Confidencialidade e Proteção de Dados (RGPD)">
        <p>8.1. Cada parte compromete-se a tratar com estrita confidencialidade todas as informações pessoais, comerciais ou operacionais a que tenha acesso por força deste contrato.</p>
        <p>8.2. A {contractBrand(contract)} atua, no que respeita aos dados de hóspedes, como <em>subcontratante</em> de tratamento de dados, sob instruções do Cliente que mantém a qualidade de responsável pelo tratamento.</p>
        <p>8.3. Informação detalhada sobre o tratamento de dados pessoais consta da Política de Privacidade disponível em <em>luxstaymadeira.pt/privacidade</em>.</p>
      </Clause>

      <div>
        <h3 className="font-display text-[#d4af37] text-sm mb-2 flex items-center gap-1.5">
          <ImageIcon size={14} /> Cláusula 9.ª · Autorização de Utilização de Imagens
        </h3>
        {contract?.image_authorization ? (
          <div className={`rounded-md border-2 p-3 ${
            imgAuthYes ? "border-emerald-500/50 bg-emerald-500/5"
                       : "border-red-500/50 bg-red-500/5"
          }`}>
            <div className="text-[10px] tracking-[0.14em] uppercase text-[#7c8e8b]">
              Decisão expressa do Cliente
            </div>
            <div className={`mt-1 font-display text-sm ${
              imgAuthYes ? "text-emerald-300" : "text-red-300"
            }`}>
              {imgAuthYes
                ? "AUTORIZA a utilização das imagens das propriedades para fins promocionais."
                : imgAuthNo
                  ? "NÃO AUTORIZA a utilização de imagens das propriedades para fins promocionais."
                  : "—"}
            </div>
          </div>
        ) : (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-[12px] text-amber-300">
            Aguardar a sua decisão (acima).
          </div>
        )}
        <p className="mt-2 text-[11.5px] text-[#7c8e8b] text-justify">
          A autorização cobre fotografias e vídeos das propriedades em materiais promocionais
          (site, redes sociais, portfólio, brochuras), nunca incluindo objetos pessoais
          identificáveis dos hóspedes. Pode ser revogada por escrito a qualquer momento.
        </p>
      </div>

      <Clause n="10" title="Responsabilidade Civil e Limitações">
        <p>10.1. A {contractBrand(contract)} é responsável pelos danos causados de forma direta e exclusiva pela sua atuação dolosa ou gravemente negligente, até ao limite do valor do serviço em que o dano se verificou, com o máximo de 2.500€ por evento, salvo cobertura por seguro próprio.</p>
        <p>10.2. A {contractBrand(contract)} não responde por danos pré-existentes, desgaste normal, defeitos estruturais, falhas de utilities ou condutas de hóspedes.</p>
        <p>10.3. Qualquer reclamação relativa a um serviço prestado deve ser comunicada por escrito no prazo máximo de 7 (sete) dias após a sua execução.</p>
      </Clause>

      <Clause n="11" title="Comunicações entre as Partes">
        <p>11.1. As comunicações vinculativas no âmbito do presente contrato são efetuadas por <strong>email</strong> para os endereços indicados na Identificação das Partes, considerando-se eficazes a partir do envio.</p>
        <p>11.2. Comunicações urgentes operacionais podem ser efetuadas por WhatsApp ou telefone, sendo posteriormente confirmadas por email caso envolvam alterações contratuais.</p>
      </Clause>

      <Clause n="12" title="Foro e Lei Aplicável">
        <p>12.1. O presente contrato é regido pela <strong>lei portuguesa</strong>.</p>
        <p>12.2. Para a resolução de quaisquer litígios emergentes da interpretação ou execução do presente contrato, é competente o foro da Comarca da Madeira (Funchal), com expressa renúncia a qualquer outro.</p>
        <p>12.3. As partes comprometem-se a procurar uma solução amigável dos diferendos antes de recorrer à via judicial.</p>
      </Clause>

      {contract?.notes && (
        <Clause n="13" title="Notas Adicionais">
          <p className="whitespace-pre-line">{contract.notes}</p>
        </Clause>
      )}

      <div className="text-[11px] text-[#7c8e8b] italic text-center pt-3 border-t border-[#1a4640]">
        Texto integral do contrato · Versão {CONTRACT_VERSION}
      </div>
    </div>
  );
}
